import { AxisCode, DeliverableType } from '@atlas/types'
import type { CampaignDownloadCapabilityState, CampaignFile } from '@/domain/campaign.types'

// Pure derivation of Campaign download-capability state from the Files the
// Campaign has already fetched. No backend route exposes capability state, so
// none is invented: there is no HTTP call, no React Query key, and no
// fixture-scenario lookup anywhere in this module.

/** A File is a distribution package only when it carries the `deliverable_type = distribution_package` tag. */
export function isDistributionPackageFile(file: CampaignFile): boolean {
	return file.tags.some(
		(tag) => tag.axisCode === AxisCode.DELIVERABLE_TYPE && tag.valueCode === DeliverableType.DISTRIBUTION_PACKAGE,
	)
}

/**
 * Derive the four independent availability values from the associated Files.
 * Empty or not-yet-loaded Files data yields unavailable modes. `package:
 * 'available'` reports only that an existing distribution-package File is
 * present — it never enables package *generation*, which has no route.
 * `selected`/`all` report whether the synchronous bulk-download endpoint is
 * reachable at all (there is at least one File to consider); the selection-
 * count gate for `Download selected` specifically is owned by
 * `CampaignDownloadMenu`, not this pure derivation. `files` is not mutated.
 */
export function deriveCampaignDownloadCapabilities(files: readonly CampaignFile[]): CampaignDownloadCapabilityState {
	const hasFiles = files.length > 0
	return {
		individual: hasFiles ? 'available' : 'unavailable',
		selected: hasFiles ? 'available' : 'unavailable',
		all: hasFiles ? 'available' : 'unavailable',
		package: files.some(isDistributionPackageFile) ? 'available' : 'unavailable',
	}
}
